import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Search, Users, Sparkles, ShieldOff, Crown } from "lucide-react";

const PRESETS = [
  { label: "30d",  days: 30 },
  { label: "90d",  days: 90 },
  { label: "1yr",  days: 365 },
  { label: "Lifetime", days: 3650 },
];

export default function StudentsAdmin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [stateFilter, setStateFilter] = useState("all");
  const [subFilter, setSubFilter] = useState("all"); // all | active | inactive
  const [pending, setPending] = useState({}); // { userId: true } during grant/revoke

  const load = () =>
    api.get("/admin/students").then((r) => setRows(r.data)).finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const states = useMemo(() => {
    const s = new Set(rows.map((r) => r.state).filter(Boolean));
    return ["all", ...Array.from(s).sort()];
  }, [rows]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (stateFilter !== "all" && r.state !== stateFilter) return false;
      if (subFilter === "active" && !r.subscription_active) return false;
      if (subFilter === "inactive" && r.subscription_active) return false;
      if (!query) return true;
      return (
        (r.name || "").toLowerCase().includes(query) ||
        (r.email || "").toLowerCase().includes(query) ||
        (r.phone || "").includes(query) ||
        (r.state || "").toLowerCase().includes(query)
      );
    });
  }, [rows, q, stateFilter, subFilter]);

  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");

  const grantPreset = async (r, days, label) => {
    if (!window.confirm(`Give ${r.name} ${label} of free access (no payment)?`)) return;
    setPending((p) => ({ ...p, [r.id]: true }));
    try {
      await api.post(`/admin/subscription/${r.id}/grant`, { days });
      toast.success(`${label} granted to ${r.name}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to grant");
    } finally {
      setPending((p) => { const q = { ...p }; delete q[r.id]; return q; });
    }
  };

  const grantCustom = async (r) => {
    const daysStr = window.prompt(`Grant ${r.name} how many days of free access?`, "30");
    if (!daysStr) return;
    const days = parseInt(daysStr, 10);
    if (!Number.isFinite(days) || days < 1) { toast.error("Enter a valid number"); return; }
    setPending((p) => ({ ...p, [r.id]: true }));
    try {
      await api.post(`/admin/subscription/${r.id}/grant`, { days });
      toast.success(`Granted ${days} days to ${r.name}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to grant");
    } finally {
      setPending((p) => { const q = { ...p }; delete q[r.id]; return q; });
    }
  };

  const revoke = async (r) => {
    if (!window.confirm(`Revoke ${r.name}'s access immediately?`)) return;
    setPending((p) => ({ ...p, [r.id]: true }));
    try {
      await api.post(`/admin/subscription/${r.id}/revoke`);
      toast.success(`Access revoked for ${r.name}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to revoke");
    } finally {
      setPending((p) => { const q = { ...p }; delete q[r.id]; return q; });
    }
  };

  const activeCount = rows.filter((r) => r.subscription_active).length;

  return (
    <div className="space-y-6" data-testid="admin-students-panel">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-neutral-500 mb-1">Student roster · Free access controls</div>
          <h2 className="text-2xl font-black tracking-tight flex items-center gap-2">
            <Users className="w-5 h-5" /> {rows.length} total · {filtered.length} shown · <span className="text-emerald-700">{activeCount} with access</span>
          </h2>
          <div className="text-[11px] font-mono text-muted-foreground mt-2 max-w-2xl">
            Admin can grant access to any student without payment. Click a preset (30d / 90d / 1yr / Lifetime) or "Custom" for a specific number of days. "Revoke" instantly ends access.
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <div className="relative min-w-[240px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name / email / phone…"
              className="rounded-none h-11 pl-9 font-mono border-black/20"
              data-testid="admin-students-search"
            />
          </div>
          <select
            value={subFilter}
            onChange={(e) => setSubFilter(e.target.value)}
            className="border border-black/20 h-11 px-3 font-mono text-sm bg-white"
            data-testid="admin-students-sub-filter"
          >
            <option value="all">All students</option>
            <option value="active">Has access</option>
            <option value="inactive">No access</option>
          </select>
          <select
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value)}
            className="border border-black/20 h-11 px-3 font-mono text-sm bg-white"
            data-testid="admin-students-state-filter"
          >
            {states.map((s) => (
              <option key={s} value={s}>{s === "all" ? "All states" : s}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="font-mono text-neutral-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="border-2 border-dashed border-black/20 p-10 text-center font-mono text-neutral-500" data-testid="admin-students-empty">
          {rows.length === 0 ? "No students yet." : "No students match your filters."}
        </div>
      ) : (
        <div className="border border-black/15 overflow-x-auto" data-testid="admin-students-table">
          <table className="w-full text-sm font-mono min-w-[1100px]">
            <thead className="bg-[#F4F4F5]">
              <tr>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-[0.2em] font-bold">Name</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-[0.2em] font-bold">Contact</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-[0.2em] font-bold">State</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-[0.2em] font-bold">Access</th>
                <th className="text-right px-4 py-3 text-xs uppercase tracking-[0.2em] font-bold">Attempts</th>
                <th className="text-right px-4 py-3 text-xs uppercase tracking-[0.2em] font-bold">Avg Acc</th>
                <th className="text-right px-4 py-3 text-xs uppercase tracking-[0.2em] font-bold">Avg WPM</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-[0.2em] font-bold">Grant free access</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-t border-black/10" data-testid={`admin-student-row-${r.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-bold">{r.name}</div>
                    <div className="text-[10px] text-neutral-500 truncate max-w-[220px]" title={r.address}>{r.address || "—"}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div>{r.phone || "—"}</div>
                    <div className="text-[10px] text-neutral-500">{r.email}</div>
                  </td>
                  <td className="px-4 py-3">{r.state || "—"}</td>
                  <td className="px-4 py-3">
                    {r.subscription_active ? (
                      <div className="inline-flex items-center gap-1.5 px-2 py-1 border-2 border-emerald-500 bg-emerald-50 text-emerald-700 text-[10px] uppercase tracking-widest font-bold rounded" data-testid={`admin-sub-badge-${r.id}`}>
                        <Crown className="w-3 h-3" /> Active · {r.subscription_days_left}d left
                      </div>
                    ) : (
                      <div className="inline-flex items-center gap-1.5 px-2 py-1 border border-black/20 bg-white text-neutral-500 text-[10px] uppercase tracking-widest font-bold rounded" data-testid={`admin-sub-badge-${r.id}`}>
                        No access
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-bold">{r.attempts}</td>
                  <td className="px-4 py-3 text-right text-green-700">{r.avg_accuracy}%</td>
                  <td className="px-4 py-3 text-right text-[#002FA7]">{r.avg_wpm}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {PRESETS.map((p) => (
                        <Button
                          key={p.label}
                          onClick={() => grantPreset(r, p.days, p.label)}
                          disabled={!!pending[r.id]}
                          variant="outline"
                          size="sm"
                          className="rounded-none h-8 px-2 border-brand-gold text-brand-gold hover:bg-brand-gold hover:text-white text-[10px] font-bold tracking-wider"
                          data-testid={`admin-grant-${p.label.toLowerCase()}-${r.id}`}
                          title={`Grant ${p.label} of free access`}
                        >
                          <Sparkles className="w-3 h-3 mr-1" /> {p.label}
                        </Button>
                      ))}
                      <Button
                        onClick={() => grantCustom(r)}
                        disabled={!!pending[r.id]}
                        variant="outline"
                        size="sm"
                        className="rounded-none h-8 px-2 border-black/30 text-neutral-700 hover:bg-neutral-100 text-[10px] font-bold tracking-wider"
                        data-testid={`admin-grant-custom-${r.id}`}
                      >
                        Custom
                      </Button>
                      {r.subscription_active && (
                        <Button
                          onClick={() => revoke(r)}
                          disabled={!!pending[r.id]}
                          variant="outline"
                          size="sm"
                          className="rounded-none h-8 px-2 border-red-400 text-red-700 hover:bg-red-50 text-[10px] font-bold tracking-wider"
                          data-testid={`admin-revoke-${r.id}`}
                        >
                          <ShieldOff className="w-3 h-3 mr-1" /> Revoke
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
